import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import {
  Accordion,
  Col,
  FilterAccordionHeader,
  Label,
  MultiSelection,
  RepeatableField,
  Row,
  Select,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import FieldFilter from '../advanced/fields/filters/components/FieldFilter';
import OperatorField from '../advanced/fields/filters/components/OperatorField';
import ValueField from '../advanced/fields/filters/components/ValueField';
import {
  buildCubeMembersFromMeta,
} from './utils/viewMetaUtils';

/**
 * Generic section selector for view-based builders.
 *
 * section.fields is required — hard-coded field keys per section.
 * Labels resolved via: aliasMember → labelsMap → fallback humanize.
 * cubeMembers for filters uses full meta (all view fields available for filtering).
 */
function ViewSectionSelector({
  section,
  viewName,
  metaResponse,
  labelsMap,
  fieldAliasMap,
  formValues,
}) {
  const [open, setOpen] = useState(true);
  const intl = useIntl();

  // Resolve label: aliasMember key → labelsMap → direct key → humanize fallback
  const resolveFieldLabel = (fieldKey) => {
    const alias = fieldAliasMap?.[fieldKey];
    if (alias && labelsMap?.[alias]) return labelsMap[alias];
    if (labelsMap?.[fieldKey]) return labelsMap[fieldKey];
    const bare = fieldKey.includes('.') ? fieldKey.split('.').pop() : fieldKey;
    return bare.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // dataOptions from hard-coded section.fields
  const dataOptions = (section.fields || []).map(fieldKey => ({
    value: fieldKey,
    label: resolveFieldLabel(fieldKey),
  }));

  const cubeMembers = buildCubeMembersFromMeta(
    metaResponse,
    viewName,
    labelsMap,
  );

  const filtersField = section.filtersField || `${section.formField}Filters`;
  const operatorField = `${section.formField}FilterOperator`;
  const isMeasure = section.fieldType === 'measure';

  const renderFilter = (field, index) => {
    const selectedMember = formValues?.[filtersField]?.[index]?.member;
    const selectedOperator = formValues?.[filtersField]?.[index]?.operator;
    const isDateRange = ['inDateRange', 'notInDateRange'].includes(
      selectedOperator,
    );
    const cubeFieldType = isMeasure ? 'measures' : 'dimensions';

    return (
      <>
        <Row>
          <Col xs={4}>
            <FieldFilter
              field={field}
              index={index}
              currentCubeMembers={cubeMembers}
              disabled={false}
              fieldType={cubeFieldType}
            />
          </Col>
          <Col xs={4}>
            <OperatorField
              field={field}
              index={index}
              currentCubeMembers={cubeMembers}
              disabled={false}
              fieldType={cubeFieldType}
              selectedMember={selectedMember}
            />
          </Col>
          {!isDateRange && (
            <Col xs={4}>
              <ValueField
                field={field}
                index={index}
                currentCubeMembers={cubeMembers}
                disabled={false}
                fieldType={cubeFieldType}
                selectedMember={selectedMember}
                selectedOperator={selectedOperator}
                formValues={formValues}
              />
            </Col>
          )}
        </Row>
        {isDateRange && (
          <Row>
            <Col xs={12}>
              <ValueField
                field={field}
                index={index}
                currentCubeMembers={cubeMembers}
                disabled={false}
                fieldType={cubeFieldType}
                selectedMember={selectedMember}
                selectedOperator={selectedOperator}
                formValues={formValues}
              />
            </Col>
          </Row>
        )}
      </>
    );
  };

  return (
    <Accordion
      id={`view-section-${section.formField}`}
      label={<h2>{section.label}</h2>}
      open={open}
      onToggle={() => setOpen((o) => !o)}
      separator
      header={FilterAccordionHeader}
    >
      {section.description && (
        <div style={{ marginBottom: '1rem' }}>{section.description}</div>
      )}

      <Field
        name={section.formField}
        component={MultiSelection}
        label={section.selectionLabel || section.label}
        placeholder={intl.formatMessage({
          id: 'ui-reports.viewBuilder.chooseFields',
          defaultMessage: 'Choose fields...',
        })}
        dataOptions={dataOptions}
      />

      <Label>
        <FormattedMessage
          id="ui-reports.viewBuilder.filters"
          defaultMessage="Filters"
        />
      </Label>

      {formValues?.[filtersField]?.length > 1 && (
        <Row>
          <Col xs={4}>
            <Field
              name={operatorField}
              component={Select}
              label={
                <FormattedMessage
                  id="ui-reports.viewBuilder.filterOperator"
                  defaultMessage="Filter Operator"
                />
              }
              dataOptions={[
                {
                  value: 'and',
                  label: intl.formatMessage({
                    id: 'ui-reports.viewBuilder.filterOperator.and',
                    defaultMessage: 'AND (all conditions)',
                  }),
                },
                {
                  value: 'or',
                  label: intl.formatMessage({
                    id: 'ui-reports.viewBuilder.filterOperator.or',
                    defaultMessage: 'OR (any condition)',
                  }),
                },
              ]}
            />
          </Col>
        </Row>
      )}

      <FieldArray
        name={filtersField}
        component={RepeatableField}
        onAdd={(fields) => fields.push('')}
        addLabel={intl.formatMessage({
          id: 'ui-reports.viewBuilder.addFilter',
          defaultMessage: 'Add filter',
        })}
        renderField={renderFilter}
      />
    </Accordion>
  );
}

ViewSectionSelector.propTypes = {
  section: PropTypes.shape({
    formField:    PropTypes.string.isRequired,
    filtersField: PropTypes.string,
    label:        PropTypes.node.isRequired,
    selectionLabel: PropTypes.node,
    description:  PropTypes.node,
    fields:       PropTypes.arrayOf(PropTypes.string).isRequired,
    fieldType:    PropTypes.oneOf(['dimension', 'measure']),
  }).isRequired,
  viewName:      PropTypes.string.isRequired,
  metaResponse:  PropTypes.object,
  labelsMap:     PropTypes.object,
  fieldAliasMap: PropTypes.object,
  formValues:    PropTypes.object,
};

export default ViewSectionSelector;
